import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import sv from './locales/sv.json';
import en from './locales/en.json';
import { detectLanguage, DEFAULT_LANGUAGE, storeLanguage, setActiveLanguage, normaliseLanguage, hasStoredLanguage, getPendingLanguage } from './languages';
import { applyDateLocale } from './dates';

i18n.use(initReactI18next).init({
    resources: {
        sv: { translation: sv },
        en: { translation: en }
    },
    lng: detectLanguage(),
    // Swedish is the source of truth: a key missing from another language degrades to Swedish,
    // never to a raw key such as "common.save".
    fallbackLng: DEFAULT_LANGUAGE,
    returnEmptyString: false,
    interpolation: {
        // React escapes for us.
        escapeValue: false
    },
    react: {
        // React 16 has no Suspense for data, and every resource is bundled synchronously anyway.
        useSuspense: false
    }
});

setActiveLanguage(i18n.language);
applyDateLocale(i18n.language);

// Persisting is deliberately NOT done here. Only a choice the user made, or one the server holds
// for them, is worth remembering on this device; a guess must not masquerade as a decision on the
// next visit. The callers below persist; applyOrganisationLanguage does not.
i18n.on('languageChanged', language => {
    setActiveLanguage(language);
    applyDateLocale(language);
});

/**
 * Adopts the language the server holds for the signed-in user.
 *
 * GET /users/validate returns an already-resolved value (user preference, then organisation
 * default, then Swedish) and never null, so there is no fallback chain to repeat here. The server
 * value wins over localStorage at sign-in, which is what makes the choice follow a user between
 * devices; localStorage still covers the pre-auth login screen, where there is no session to ask.
 *
 * A language the server knows but this build ships no resources for is ignored rather than applied,
 * so the UI never renders raw keys.
 * @param {string} language ISO 639-1 code from user.language
 * @returns {Promise<void>}
 */
export async function applyServerLanguage(language) {
    // A choice the server never accepted outranks whatever the server resolves to, because that
    // resolution cannot include a preference it failed to store. Without this the user picks a
    // language, the write is rejected, and the next reload silently puts them back.
    const pending = getPendingLanguage();

    if (pending != null) {
        if (pending !== i18n.language) {
            await i18n.changeLanguage(pending);
        }

        return;
    }

    const code = normaliseLanguage(language);

    if (code == null) {
        return;
    }

    // Remembered even when it matches the current language, so the login page on this device opens
    // in the user's language next time, before there is a session to ask.
    storeLanguage(code);

    if (code !== i18n.language) {
        await i18n.changeLanguage(code);
    }
}

/**
 * Applies the organisation's default language on the login page.
 *
 * The login page renders before there is a session, so the user's own preference is unreachable —
 * POST /organisations/search/email is the first point at which the app learns anything about who is
 * signing in. This mirrors the server's own order (user preference, then organisation, then
 * Swedish) with the browser's guess sitting below the organisation.
 *
 * It never overrides a language the user picked on this device, and it is not persisted: it is a
 * guess about an unidentified visitor, not their decision. Once they sign in, applyServerLanguage
 * replaces it with the real preference and stores that instead.
 * @param {string} language ISO 639-1 code from the organisation lookup
 * @returns {Promise<void>}
 */
export async function applyOrganisationLanguage(language) {
    if (hasStoredLanguage()) {
        return;
    }

    const code = normaliseLanguage(language);

    if (code == null || code === i18n.language) {
        return;
    }

    await i18n.changeLanguage(code);
}

export default i18n;
